import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Wrench, TrendingUp, Building } from 'lucide-react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import ImageCarousel from '../components/ImageCarousel';
import SEOHead from '../components/SEOHead';
import { companies, heroSlides, projectPlaceholders } from '../data/mockData';

const Home = () => {
  const [ref1, inView1] = useInView({ threshold: 0.3, triggerOnce: true });
  const [ref2, inView2] = useInView({ threshold: 0.3, triggerOnce: true });
  const [ref3, inView3] = useInView({ threshold: 0.3, triggerOnce: true });

  // Scroll to top on mount
  React.useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const subsidiaries = [companies.energiya, companies.mynah, companies.mk];
  const iconMap = {
    energiya: Wrench,
    mynah: TrendingUp,
    mk: Building
  };

  return (
    <div className="min-h-screen bg-white">
      <SEOHead 
        title="immoriya Projektentwicklung GmbH | Immobilienwerte in Osnabrück"
        description="immoriya ist Ihre strategische Immobilien-Holding in Osnabrück. Projektentwicklung, energetische Sanierung durch Energiya, Immobilienhandel über MYNAH und Immobilienverwaltung durch MK."
        canonical="https://immoriya.de/"
      />

      {/* Hero Carousel */}
      <ImageCarousel slides={heroSlides} autoplay={true} interval={6000} />

      {/* Holding Introduction */}
      <section className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 via-white to-gray-50">
        <motion.div
          ref={ref1}
          initial={{ opacity: 0, y: 50 }}
          animate={inView1 ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 1, ease: "easeOut" }}
          className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 text-center"
        >
          <div className="mb-12">
            <img 
              src="https://customer-assets.emergentagent.com/job_gruppe-osnabrueck/artifacts/bjvkd39u_69a21436-daff-4718-9fb3-cf55e72e960d.jpeg"
              alt="immoriya Logo"
              className="h-20 mx-auto mb-8"
            />
          </div>
          
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6 leading-tight">
            {companies.holding.tagline}
          </h2>
          
          <p className="text-lg md:text-xl text-gray-600 mb-8 max-w-3xl mx-auto leading-relaxed">
            {companies.holding.description}
          </p>

          <div className="flex flex-wrap justify-center gap-3 mb-12">
            {companies.holding.operations.map((op, idx) => (
              <span 
                key={idx}
                className="px-4 py-2 bg-blue-100 text-blue-700 rounded-full text-sm font-medium"
              >
                {op}
              </span>
            ))}
          </div>

          <Link to="/kontakt">
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className="px-8 py-3 bg-blue-600 hover:bg-blue-700 text-white text-base font-semibold rounded-full shadow-lg transition-all"
            >
              Beratung anfragen
              <ArrowRight className="inline-block ml-2 w-4 h-4" />
            </motion.button>
          </Link>
        </motion.div>
      </section>

      {/* Unternehmensgruppe */}
      <section className="min-h-screen flex items-center bg-white py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full">
          <motion.div
            ref={ref2}
            initial={{ opacity: 0, y: 50 }}
            animate={inView2 ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 1 }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Die Unternehmensgruppe
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Drei spezialisierte Gesellschaften – eine strategische Vision
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {subsidiaries.map((company, idx) => {
              const Icon = iconMap[company.id];
              return (
                <motion.div
                  key={company.id}
                  initial={{ opacity: 0, y: 50 }}
                  animate={inView2 ? { opacity: 1, y: 0 } : {}}
                  transition={{ duration: 0.8, delay: idx * 0.2 }}
                >
                  <Link to={`/${company.id}`}>
                    <div 
                      className="group relative h-[600px] rounded-2xl overflow-hidden shadow-2xl cursor-pointer transform transition-all duration-500 hover:scale-105"
                      style={{ 
                        background: `linear-gradient(135deg, ${company.color}15 0%, ${company.color}05 100%)`
                      }}
                    >
                      <div className="absolute top-8 right-8 w-20 h-20 rounded-full flex items-center justify-center"
                        style={{ backgroundColor: `${company.color}20` }}
                      >
                        <Icon className="w-10 h-10" style={{ color: company.color }} />
                      </div>

                      <div className="absolute bottom-0 left-0 right-0 p-8 bg-gradient-to-t from-black/80 via-black/60 to-transparent text-white">
                        <div className="mb-4">
                          <span className="px-4 py-2 text-sm font-semibold rounded-full"
                            style={{ backgroundColor: company.color }}
                          >
                            {company.ownership}
                          </span>
                        </div>
                        
                        <h3 className="text-2xl font-bold mb-2">
                          {company.name}
                        </h3>
                        
                        <p className="text-lg mb-3 opacity-90">
                          {company.tagline}
                        </p>
                        
                        <p className="text-sm text-gray-200 leading-relaxed mb-4">
                          {company.description}
                        </p>

                        <div className="flex items-center text-white group-hover:translate-x-2 transition-transform text-sm">
                          <span className="font-semibold">Mehr erfahren</span>
                          <ArrowRight className="ml-2 w-4 h-4" />
                        </div>
                      </div>

                      <div 
                        className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500"
                        style={{ 
                          background: `linear-gradient(135deg, ${company.color}40 0%, ${company.color}20 100%)`
                        }}
                      />
                    </div>
                  </Link>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Portfolio */}
      <section className="min-h-screen flex items-center bg-gray-50 py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full">
          <motion.div
            ref={ref3}
            initial={{ opacity: 0, y: 50 }}
            animate={inView3 ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 1 }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Portfolio
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Einblick in unsere Immobilien
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {projectPlaceholders.map((project, idx) => (
              <motion.div
                key={idx}
                initial={{ opacity: 0, scale: 0.9 }}
                animate={inView3 ? { opacity: 1, scale: 1 } : {}}
                transition={{ duration: 0.6, delay: idx * 0.15 }}
                className="relative h-[500px] rounded-2xl overflow-hidden shadow-xl group"
              >
                <img 
                  src={project.image}
                  alt={project.title}
                  className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent flex items-end">
                  <div className="p-6 text-white">
                    <span className="px-3 py-1 bg-white/20 backdrop-blur-sm rounded-full text-xs font-semibold mb-2 inline-block">
                      {project.type}
                    </span>
                    <h3 className="text-lg font-bold mb-1">{project.title}</h3>
                    <p className="text-sm text-gray-200">{project.description}</p>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="min-h-[60vh] flex items-center justify-center bg-gradient-to-br from-blue-600 via-blue-700 to-blue-800 text-white">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center py-16">
          <h2 className="text-3xl md:text-4xl font-bold mb-6">
            Bei Fragen stehen wir Ihnen gerne zur Verfügung
          </h2>
          <p className="text-lg mb-8 opacity-95 leading-relaxed">
            Für Informationen zu unseren Gesellschaften und Leistungen kontaktieren Sie uns gerne.
          </p>
          
          <Link to="/kontakt">
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className="px-8 py-3 bg-white text-blue-600 text-base font-semibold rounded-full shadow-lg hover:shadow-xl transition-all"
            >
              Kontakt
              <ArrowRight className="inline-block ml-2 w-4 h-4" />
            </motion.button>
          </Link>
        </div>
      </section>
    </div>
  );
};

export default Home;
